import { useMemo } from "react";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Button, LoadingOverlay } from "@covertree/ui";

import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useAppSelector } from "@redux/hooks/useAppSelector";
import { selectAddedCoverageDetails } from "@redux/coverages/coveragesSlice";
import { useCancelTllCoverageMutation } from "@redux/coverages/coveragesApi";
import { formatAwsDateString } from "@covertree/utils";
import {
  BASE_PROOF_OF_COVERAGE_PATH,
  PROOF_OF_COVERAGE_ROUTES,
} from "@routing/Residents/ProofOfCoverage";
import { RESIDENTS_ROUTES } from "@routing/Residents";

export default function ScheduledTLLFound() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { residentId: id } = useParams();

  const { updatedResident: resident, alreadyAddedCoverageId } = useAppSelector(
    selectAddedCoverageDetails
  );

  const tllEffectiveDate = useMemo(
    () =>
      resident?.coverages?.find(
        (resident) => resident.id === alreadyAddedCoverageId
      )?.policyEffectiveDate,
    [alreadyAddedCoverageId, resident]
  );

  const tllCancellationDate = useMemo(() => {
    const now = new Date(
      new Date().toLocaleString("en-US", { timeZone: "America/New_York" })
    );

    return new Date(
      now.getFullYear(),
      now.getMonth() + 2,
      1
    ).toLocaleDateString("en-US");
  }, []);

  const goForward = () => {
    navigate(
      {
        pathname: `../../../${RESIDENTS_ROUTES.RESIDENT}/${encodeURIComponent(id)}/${BASE_PROOF_OF_COVERAGE_PATH}/${PROOF_OF_COVERAGE_ROUTES.COVERAGE_ADDED}`,
        search: createSearchParams(searchParams).toString(),
      },
      { replace: true }
    );
  };

  const [cancelTllCoverage, { isSuccess, isLoading, isError }] =
    useCancelTllCoverageMutation();

  const handleCancelTll = () => {
    cancelTllCoverage({
      coverageId: alreadyAddedCoverageId,
      residentId: resident.id,
    });
  };

  if (isLoading) return <LoadingOverlay />;
  if (isError)
    return (
      <Typography>
        Could not cancel Brl Coverage. Please check your internet connection or
        contact our team.
      </Typography>
    );

  if (isSuccess) {
    goForward();
    return <LoadingOverlay />;
  }

  return (
    <Grid
      container
      display="flex"
      direction="column"
      gap={4}
      alignItems="flex-start"
      justifyContent="space-around">
      <img
        src="/images/icons/checkmark.webp"
        alt="success"
        width={80}
        height={80}
      />
      <Grid item xs={12}>
        <Typography fontSize={32}>Issued BRL policy found</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography fontSize={20}>
          A BRL policy was issued for the resident effective{" "}
          <Typography component="span" color="secondary.main">
            {formatAwsDateString(tllEffectiveDate) || ""}
          </Typography>
          . Do you want to schedule the cancellation of the BRL policy effective{" "}
          <Typography component="span" color="secondary.main">
            {tllCancellationDate}
          </Typography>
          ?
        </Typography>
      </Grid>
      <Grid item container gap={2}>
        <Button onClick={handleCancelTll}>Cancel BRL Insurance Policy</Button>
        <Button onClick={goForward} variant="outlined">
          Keep Resident in BRL
        </Button>
      </Grid>
    </Grid>
  );
}
